import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, ShieldCheck } from "lucide-react";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PADPULSE_PROMISE } from "@shared/padpulse-promise";
import { formatUsd } from "@/components/pricing/plan-selection";

export interface GuaranteeState {
  firstPaidInvoiceAt: string | null;
  eligibleUntil: string | null;
  used: boolean;
  eligible: boolean;
}

const MIN_ANSWER_LENGTH = 10;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

/**
 * The PadPulse Promise on billing settings: guarantee status (active window /
 * used / expired / not-yet-charged) and — only while eligible — the
 * self-serve refund, gated behind the required three-question exit survey.
 * A successful refund archives the account and ends the session, so the
 * dialog hard-redirects to the homepage afterwards.
 */
export function GuaranteeStatusCard({
  guarantee,
  trialOver = false,
}: {
  guarantee: GuaranteeState;
  /** The local trial has ended without a paid invoice — "during your trial" copy no longer applies. */
  trialOver?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const status: "eligible" | "used" | "expired" | "pre_charge" = guarantee.used
    ? "used"
    : guarantee.eligible
      ? "eligible"
      : guarantee.firstPaidInvoiceAt
        ? "expired"
        : "pre_charge";

  return (
    <Card data-testid="card-guarantee-status">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldCheck size={18} className={status === "eligible" ? "text-emerald-600" : "text-muted-foreground"} />
          {PADPULSE_PROMISE.name}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {status === "eligible" && guarantee.eligibleUntil && (
          <>
            <p className="text-sm text-emerald-700 font-medium" data-testid="text-guarantee-active">
              {PADPULSE_PROMISE.activeBadge(formatDate(guarantee.eligibleUntil))}
            </p>
            <p className="text-sm text-muted-foreground">{PADPULSE_PROMISE.fine}</p>
            <Button
              variant="outline"
              size="sm"
              className="border-red-200 text-red-700 hover:bg-red-50"
              onClick={() => setOpen(true)}
              data-testid="button-request-guarantee-refund"
            >
              Request my full refund
            </Button>
          </>
        )}
        {status === "pre_charge" && (
          <p className="text-sm text-muted-foreground" data-testid="text-guarantee-pre-charge">
            Your first paid invoice will be covered by a 30-day money-back guarantee.
            {trialOver ? "" : " During your trial, you can simply cancel — you'll never be billed."}
          </p>
        )}
        {status === "expired" && (
          <p className="text-sm text-muted-foreground" data-testid="text-guarantee-expired">
            The 30-day guarantee window on your first invoice has ended
            {guarantee.eligibleUntil ? ` (${formatDate(guarantee.eligibleUntil)})` : ""}.
          </p>
        )}
        {status === "used" && (
          <p className="text-sm text-muted-foreground" data-testid="text-guarantee-used">
            The guarantee has been used on this account.
          </p>
        )}
      </CardContent>

      <GuaranteeRefundDialog open={open} onOpenChange={setOpen} />
    </Card>
  );
}

/**
 * The PadPulse Promise refund dialog: the required three-question exit survey,
 * then POST /api/app/billing/guarantee-refund. Legacy (modular) accounts are
 * archived by a refund, so the dialog hard-redirects home afterwards. v3
 * accounts (annual / lifetime within 30 days) keep their account and data, so
 * `variant="v3"` refreshes billing state instead.
 */
export function GuaranteeRefundDialog({
  open,
  onOpenChange,
  variant = "legacy",
  onRefunded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: "legacy" | "v3";
  onRefunded?: () => void;
}) {
  const { toast } = useToast();
  const [answers, setAnswers] = useState<Record<string, string>>({ liked: "", disliked: "", wished: "" });

  const refundMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/billing/guarantee-refund", {
        liked: answers.liked.trim(),
        disliked: answers.disliked.trim(),
        wished: answers.wished.trim(),
        confirm: true,
      });
      return res.json() as Promise<{ refundedAmountCents?: number; message?: string }>;
    },
    onSuccess: (data) => {
      toast({
        title: "Refund on its way",
        description: `${formatUsd(data.refundedAmountCents ?? 0)} is being returned to your card. Your data stays safe if you ever come back.`,
      });
      if (variant === "v3") {
        onOpenChange(false);
        onRefunded?.();
        return;
      }
      // The account is archived and the session ended server-side.
      setTimeout(() => {
        window.location.href = "/";
      }, 2500);
    },
    onError: (err: Error) => {
      toast({
        title: "Could not process the refund",
        description: parseErrorMessage(err, "Please contact support@padpulse.io and we'll sort it out by hand."),
        variant: "destructive",
      });
    },
  });

  const answersValid = PADPULSE_PROMISE.offboardingQuestions.every(
    (q) => (answers[q.key] ?? "").trim().length >= MIN_ANSWER_LENGTH,
  );

  return (
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (refundMutation.isPending) return;
          onOpenChange(next);
        }}
      >
        <DialogContent
          className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
          data-testid="dialog-guarantee-refund"
          onInteractOutside={(e) => {
            if (refundMutation.isPending) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (refundMutation.isPending) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (refundMutation.isPending) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Before you go — 60 seconds, three questions</DialogTitle>
            <DialogDescription>
              {variant === "v3"
                ? "Your full payment comes back, no retention call. These answers genuinely shape what we build next."
                : "Your full first invoice comes back, no retention call. These answers genuinely shape what we build next."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {PADPULSE_PROMISE.offboardingQuestions.map((q) => (
              <div key={q.key} className="space-y-1.5">
                <Label htmlFor={`offboarding-${q.key}`}>{q.label}</Label>
                <Textarea
                  id={`offboarding-${q.key}`}
                  value={answers[q.key] ?? ""}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [q.key]: e.target.value }))}
                  rows={3}
                  placeholder="A sentence or two is perfect."
                  data-testid={`textarea-offboarding-${q.key}`}
                />
              </div>
            ))}
            <p className="text-xs text-muted-foreground">
              {variant === "v3"
                ? "Submitting refunds your full payment and ends your paid plan. Your account and data stay in place."
                : "Submitting refunds your entire first invoice, cancels your subscription immediately, and archives your account. All your data is kept safe if you ever want to return."}
            </p>
            <Button
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold"
              disabled={!answersValid || refundMutation.isPending}
              onClick={() => refundMutation.mutate()}
              data-testid="button-confirm-guarantee-refund"
            >
              {refundMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {refundMutation.isPending
                ? "Processing your refund…"
                : variant === "v3"
                  ? "Refund my payment"
                  : "Refund my first invoice & close my account"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
  );
}
